import { useEffect, useState } from 'react';
import { CalendarDate, parseDate } from '@internationalized/date';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { DecorativeBankIcon } from '@interxfi/design-system/icons/decorative/bank/decorative-bank-icon';
import { DecorativeEquitiesIcon } from '@interxfi/design-system/icons/decorative/equities/decorative-equities-icon';
import { DecorativeSweepIcon } from '@interxfi/design-system/icons/decorative/sweep/decorative-sweep-icon';
import {
  IxfHoursV1DayOfWeek,
  type IxfHoursV1DayTimeWindow,
  type IxfHoursV1OperatingDay,
  type IxfHoursV1SessionHours,
  type IxfHoursV1TimeWindow,
} from '@interxfi/types';
import { getCurrentDate } from '@interxfi/utilities/date/current';
import { NY_TIMEZONE } from '@interxfi/utilities/date/time-zones';
import { getIntlDateTimeFormat } from '@interxfi/utilities/intl/date';
import { parseNumber } from '@interxfi/utilities/number/parse';
import type { TypedKeys } from '@interxfi/utilities/types/path';

export type ScheduleData = {
  [K in 'trading' | 'transfers' | 'moneyMarket']-?: NonNullable<IxfHoursV1OperatingDay[K]>;
};

export type ScheduleName = keyof ScheduleData;

export type ScheduleSession<T extends ScheduleName> = keyof ScheduleData[T];

export type SessionHours<T extends ScheduleName> = TypedKeys<ScheduleData[T], IxfHoursV1SessionHours>;

export const SCHEDULE_OPTIONS: InputDropdownOption<ScheduleName>[] = [
  {
    value: 'trading',
    label: 'Trading',
    prefix: <DecorativeEquitiesIcon aria-label="Trading icon" fill="primary" height={16} />,
  },
  {
    value: 'transfers',
    label: 'Transfers',
    prefix: <DecorativeBankIcon aria-label="Transfers icon" fill="secondary" height={16} />,
  },
  {
    value: 'moneyMarket',
    label: 'Money Markets',
    prefix: <DecorativeSweepIcon aria-label="Money Markets icon" fill="tertiary" height={16} />,
  },
];

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const MONTH_OPTIONS: InputDropdownOption[] = MONTH_NAMES.map((name, index) => ({ label: name, value: `${index + 1}` }));

export const HOURS_DAY_OF_WEEK_SHORT_LABELS: Record<IxfHoursV1DayOfWeek, string> = {
  [IxfHoursV1DayOfWeek.Sunday]: 'Sun',
  [IxfHoursV1DayOfWeek.Monday]: 'Mon',
  [IxfHoursV1DayOfWeek.Tuesday]: 'Tue',
  [IxfHoursV1DayOfWeek.Wednesday]: 'Wed',
  [IxfHoursV1DayOfWeek.Thursday]: 'Thu',
  [IxfHoursV1DayOfWeek.Friday]: 'Fri',
  [IxfHoursV1DayOfWeek.Saturday]: 'Sat',
  [IxfHoursV1DayOfWeek.Unspecified]: '',
};

export function getNextOccurringYear(timeZone: string, month: string, day: string) {
  const currentDate = getCurrentDate(timeZone);
  const selectedDateInCurrentYear = new CalendarDate(currentDate.year, Number(parseNumber(month)), Number(parseNumber(day)));

  if (selectedDateInCurrentYear.compare(currentDate) >= 0) {
    return `${currentDate.year}`;
  }

  return `${currentDate.year + 1}`;
}

export function formatTimeWindow(openTime: IxfHoursV1TimeWindow) {
  return `${openTime.starts}-${openTime.stops}`;
}

export function formatDayTimeWindow(dayTimeWindow: IxfHoursV1DayTimeWindow) {
  if (dayTimeWindow.starts.day === dayTimeWindow.stops.day) {
    return `${dayTimeWindow.starts.time}-${dayTimeWindow.stops.time}`;
  }

  return `${HOURS_DAY_OF_WEEK_SHORT_LABELS[dayTimeWindow.starts.day]} ${dayTimeWindow.starts.time} - ${HOURS_DAY_OF_WEEK_SHORT_LABELS[dayTimeWindow.stops.day]} ${dayTimeWindow.stops.time}`;
}

export function formatTimeZoneNameForDisplay(timeZone: string): string {
  const [region, city] = (timeZone || '').split('/');

  if (!city) {
    return region;
  }

  return `${region} / ${city.replace(/_/g, ' ')}`;
}

export async function getTimeZoneOptions(): Promise<InputDropdownOption[]> {
  try {
    const tzs = await import('@interxfi/utilities/date/time-zones');
    return tzs.timeZoneList.map((tz: string) => ({ label: formatTimeZoneNameForDisplay(tz), value: tz }));
  } catch {
    return [];
  }
}

export function useTimeZoneOptions() {
  const [timeZoneOptions, setTimeZoneOptions] = useState<InputDropdownOption[]>([]);

  useEffect(() => {
    void (async () => {
      const tzOptions = await getTimeZoneOptions();
      setTimeZoneOptions(tzOptions);
    })();
  }, []);

  return timeZoneOptions;
}

export function getShortTimeZoneName(timeZone: string | undefined) {
  const formatter = getIntlDateTimeFormat('en-US', { timeZone, timeZoneName: 'short' });

  return formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
}

export function getDayOfWeekForDate(date: string): IxfHoursV1DayOfWeek {
  try {
    const parsedDate = parseDate(date);
    const dayIndex = parsedDate.toDate(NY_TIMEZONE).getDay();

    switch (dayIndex) {
      case 0:
        return IxfHoursV1DayOfWeek.Sunday;
      case 1:
        return IxfHoursV1DayOfWeek.Monday;
      case 2:
        return IxfHoursV1DayOfWeek.Tuesday;
      case 3:
        return IxfHoursV1DayOfWeek.Wednesday;
      case 4:
        return IxfHoursV1DayOfWeek.Thursday;
      case 5:
        return IxfHoursV1DayOfWeek.Friday;
      case 6:
        return IxfHoursV1DayOfWeek.Saturday;
      default:
        return IxfHoursV1DayOfWeek.Sunday;
    }
  } catch {
    return IxfHoursV1DayOfWeek.Sunday;
  }
}
